import React from "react";

import LockIcon from "@mui/icons-material/Lock";
import Typography from "@mui/material/Typography";

import ReportingModule from "@app/analytics/account/WorkspaceSettings/Settings/ReportingModule";
import WorkspaceTagsModule from "@app/analytics/account/WorkspaceSettings/Settings/WorkspaceTagsModule";
import useWorkspaceSettings from "@app/analytics/hooks/workspace/useWorkspaceSettings";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { toast } from "@app/common/hooks/useToasts";
import { UpdateWorkspaceSettingsBody } from "@app/common/workspaces/types";
import { useAdminWorkspace } from "@app/common/workspaces/useWorkspace";
import { usePageTitle } from "@app/utils/hooks";

import Stack, { Row } from "@rui/Stack";

import AdvancedModule from "./AdvancedModule";
import CustomVocabularyModule from "./CustomVocabularyModule";
import EmailDigestsModule from "./EmailDigestsModule";
import IntegrationsModule from "./IntegrationsModule";
import MeetingPreferencesModule from "./MeetingPreferencesModule";
import ReadAssistantModule from "./ReadAssistantModule";
import RecordingAndPlaybackModule from "./RecordingAndPlaybackModule";
import WorkspaceActionsModule from "./WorkspaceActionsModule";
import { TabContent } from "../components/styles";
import WorkspaceHeader from "../components/WorkspaceHeader";

const Settings = () => {
  usePageTitle("Workspace Settings - Settings");

  const { canEditWorkspaceSettings } = useFeatureAccess();
  const { settings, updateWorkspaceSettings } = useWorkspaceSettings();

  const {
    uploadCustomBotLogo,
    deleteCustomBotLogo,
    workspace,
    workspaceIsInactive,
  } = useAdminWorkspace();

  const handleUpdateWorkspaceSettings = (
    settings: UpdateWorkspaceSettingsBody
  ) => {
    updateWorkspaceSettings.mutate(settings, {
      onSuccess: () => {
        toast.success("Changes saved!");
      },
      onError: () => {
        toast.error(
          "Something went wrong and your changes could not be saved. Please refresh the page or contact support@read.ai if the problem persists."
        );
      },
    });
  };

  return (
    <Stack sx={{ width: "100%", height: "100%" }} variant="left">
      <Stack sx={{ width: "100%" }}>
        <WorkspaceHeader
          title={workspace.name ?? ""}
          type="workspace"
          subtitle="Workspace Settings"
          actions={
            canEditWorkspaceSettings
              ? [
                  <Row
                    key="visibility"
                    variant="right"
                    sx={{ pr: 4, color: "shades.neutral.500" }}
                    spacing={0.5}
                  >
                    <LockIcon sx={{ fontSize: "10px" }} />
                    <Typography variant="labelReg">
                      Only visible to Workspace Owners and Admins
                    </Typography>
                  </Row>,
                ]
              : []
          }
        />
      </Stack>
      <TabContent sx={{ width: "100%" }} alignItems="center">
        <Stack
          sx={{
            width: "100%",
            borderBottom: "1px solid",
            borderColor: "shades.neutral.100",
          }}
        >
          {canEditWorkspaceSettings && (
            <>
              <MeetingPreferencesModule
                onSettingUpdate={handleUpdateWorkspaceSettings}
                settings={settings}
                workspaceIsInactive={workspaceIsInactive}
              />
              <ReadAssistantModule
                onSettingUpdate={handleUpdateWorkspaceSettings}
                onUploadCustomBotLogo={uploadCustomBotLogo}
                onDeleteCustomBotLogo={deleteCustomBotLogo}
                settings={settings}
                workspaceIsInactive={workspaceIsInactive}
              />
              <RecordingAndPlaybackModule
                onSettingUpdate={handleUpdateWorkspaceSettings}
                settings={settings}
                workspaceIsInactive={workspaceIsInactive}
              />
              <ReportingModule
                onSettingUpdate={handleUpdateWorkspaceSettings}
                settings={settings}
                workspaceIsInactive={workspaceIsInactive}
              />
              <IntegrationsModule
                onSettingUpdate={handleUpdateWorkspaceSettings}
                settings={settings}
                workspaceIsInactive={workspaceIsInactive}
              />
              <WorkspaceTagsModule
                workspaceId={workspace.id}
                workspaceIsInactive={workspaceIsInactive}
              />
              <CustomVocabularyModule
                workspaceIsInactive={workspaceIsInactive}
              />
              <EmailDigestsModule
                onSettingUpdate={handleUpdateWorkspaceSettings}
                settings={settings}
                workspaceIsInactive={workspaceIsInactive}
              />
              <AdvancedModule />
            </>
          )}
          <WorkspaceActionsModule />
        </Stack>
      </TabContent>
    </Stack>
  );
};

export default Settings;
